import type { MetaAdCopy, MetaAdType } from '../types/campaign.js';
import type { AccountCampaign } from '../types/accountCampaign.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';

// Pre-P2b-3 polish round 5 §1.2: resolve which Meta ad type a row
// actually runs. Pure helper; no I/O.
//
// Resolution rules:
//   - Campaign.metaAdType === 'traffic'   → 'traffic'  (locked, ignores
//                                            row override + location default)
//   - Campaign.metaAdType === 'lead_gen'  → 'lead_gen' (locked)
//   - Campaign.metaAdType === 'both':
//       - row.metaAdType === 'traffic'                 → 'traffic'
//       - row.metaAdType === 'lead_gen'                → 'lead_gen'
//       - row.metaAdType === null/undefined:
//           - locationProfile.defaultMetaAdType set    → that value
//           - locationProfile null/undefined or no default → null
//
// Returns null only when Campaign is 'both', row has no override, and
// the location has no default. The UI surfaces this as a "Pick an ad
// type to author content" prompt — the AM either picks on the row or
// sets the location default upstream.
//
// Defensive on inputs: row may be loading (null/undefined), location
// profile may be null when the location has no profile attached. The
// Campaign argument is required because without it there's nothing to
// resolve against.

export type EffectiveMetaAdType = 'traffic' | 'lead_gen' | null;

export function effectiveMetaAdType(
  row:
    | Pick<AccountCampaign, 'metaAdType'>
    | null
    | undefined,
  // metaAdType lives at campaign.metaAdCopy.metaAdType. Pin the structural
  // shape via Pick<MetaAdCopy, 'metaAdType'> so the helper compiles
  // against test stubs that don't supply the rest of metaAdCopy.
  campaign: { metaAdCopy: Pick<MetaAdCopy, 'metaAdType'> },
  locationProfile:
    | Pick<FranchiseLocationProfile, 'defaultMetaAdType'>
    | null
    | undefined,
): EffectiveMetaAdType {
  const campaignType: MetaAdType = campaign.metaAdCopy.metaAdType;
  // Locked branches: Campaign declares a specific ad type → that wins
  // for every row, regardless of row override or location default.
  if (campaignType === 'traffic') return 'traffic';
  if (campaignType === 'lead_gen') return 'lead_gen';

  // campaignType === 'both' — resolve via row → location → null.
  const rowOverride = row?.metaAdType;
  if (rowOverride === 'traffic' || rowOverride === 'lead_gen') {
    return rowOverride;
  }
  // Row override is null/undefined → fall through to location default.
  const locationDefault = locationProfile?.defaultMetaAdType ?? null;
  return locationDefault;
}
